import { Download, Languages, X } from "lucide-react";
import React, { useId, useState } from "react";

interface CanvasHeaderProps {
  selectedLanguage: string;
  currentLevel: number;
  historyCount: number;
  streak?: number;
  onExport: () => void;
  onClose: () => void;
}

const CanvasHeader: React.FC<CanvasHeaderProps> = ({
  selectedLanguage,
  currentLevel,
  historyCount,
  streak = 0,
  onExport,
  onClose,
}) => {
  const [showLevelHelp, setShowLevelHelp] = useState(false);
  const levelHelpId = useId();
  const exportLabel =
    historyCount === 0
      ? "No sentences to export"
      : `Export ${historyCount} sentence${historyCount === 1 ? "" : "s"}`;

  const currentStreakMod = streak % 5;
  const streakDisplay = currentStreakMod === 0 && streak > 0 ? 5 : currentStreakMod;

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0F0F14]">
      <div className="mx-auto flex min-h-16 max-w-5xl items-center justify-between gap-3 px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-[#1B1C22]">
            <Languages className="h-4 w-4 text-[#FD954F]" aria-hidden="true" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium text-white">{selectedLanguage}</div>
            <div className="flex flex-wrap items-center gap-x-2 text-xs text-white/50">
              <span>Level {currentLevel}</span>
              <button
                type="button"
                aria-expanded={showLevelHelp}
                aria-controls={levelHelpId}
                onClick={() => setShowLevelHelp((show) => !show)}
                className="rounded px-1 py-2 text-white/70 underline decoration-white/30 underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FD954F]"
              >
                How levels work
              </button>
              <div
                id={levelHelpId}
                hidden={!showLevelHelp}
                className="absolute inset-x-0 top-full mx-auto max-h-[calc(100dvh-6rem)] max-w-5xl overflow-y-auto border border-white/15 bg-[#15161B] p-4 text-base leading-relaxed text-white/80 shadow-lg sm:rounded-b-lg sm:p-6"
              >
                <p className="font-semibold text-white">Your exercise level</p>
                <p className="mt-2">
                  Levels 1–100 adjust exercise difficulty. This is not a separate points balance or
                  a measure of your overall language proficiency.
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5">
                  <li>A correct first answer raises your level by 1.</li>
                  <li>Near misses have no penalty. Correcting one still earns +1.</li>
                  <li>
                    Wrong answers lower your level by 1, then 2, then 3 for consecutive mistakes,
                    with a maximum loss of 3 per answer.
                  </li>
                  <li>
                    Correcting a wrong answer keeps the penalty and earns no extra level. A correct
                    answer resets the consecutive-mistake penalty.
                  </li>
                </ul>
                <p className="mt-3 text-sm text-white/60">
                  Each exercise can earn +1 once. Your level stays between 1 and 100.
                </p>
              </div>
            </div>
          </div>

          {streak > 0 && (
            <div
              className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-[#15161B] px-2.5 py-1 sm:flex"
              aria-label={`Commute streak: ${streak} correct in a row`}
              data-testid="commute-streak-indicator"
            >
              <span className="text-xs font-semibold tabular-nums text-[#FD954F]">{streak}</span>
              <span className="text-[11px] text-white/50">in a row</span>
              <div className="ml-1 flex items-center gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 w-2 rounded-full transition-colors ${
                      idx < streakDisplay ? "bg-[#FD954F]" : "bg-white/20"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExport}
            disabled={historyCount === 0}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-transparent px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            title={exportLabel}
            aria-label={exportLabel}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={onClose}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-[#1B1C22] px-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span>Close</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default CanvasHeader;
